/**
 * index.js — wiring for /earthquake-simulator.
 *
 * The physics lives in ../cataclysm/. This file owns the page: it turns two
 * sliders and a map click into a scenario, asks the models what happens, and
 * puts the answers where a reader will find them.
 *
 * One decision worth stating. Everything recomputes from a single `apply()`
 * rather than from a web of listeners patching bits of the DOM, because every
 * panel on this page is a different view of the SAME scenario and they must
 * never disagree — a card reading "7 баллов" over a map whose contours were
 * drawn for the previous magnitude is worse than no card at all.
 */

import { scenario, MMI_SCALE, mmiBand, GAL_PER_G, KM_PER_SP_SECOND,
         JOULES_HIROSHIMA, IPE_VALID } from '../cataclysm/quake-physics.js';
import { seismogram } from '../cataclysm/accelerogram.js';
import { buildIndex, citiesWithin, exposureByIntensity } from '../cataclysm/exposure.js';
import { CATALOGUE, BY_ID, toScenarioInput } from '../cataclysm/quake-catalogue.js';
import { createMap, paintLegend } from './map.js';
import { drawSeismogram, playRecord, stopAudio, isPlaying, AUDIO_SPEEDUP } from './seismo.js';
import * as Buildings from './buildings.js';

const CFG = window.EQ_CFG || {};
const T = CFG.t || {};
const LOC = CFG.locale || 'en';

const $ = (id) => document.getElementById(id);
const nf = (max = 0) => new Intl.NumberFormat(LOC, { maximumFractionDigits: max });
const fmt = (v, d = 0) => nf(d).format(v);

/** Compact population: 1 234 567 → "1,2 млн" is unreadable across nine
 *  locales, so Intl does it — it knows the local abbreviations. */
const compact = (v) => {
    try { return new Intl.NumberFormat(LOC, { notation: 'compact', maximumFractionDigits: 1 }).format(v); }
    catch (e) { return fmt(v); }
};

const state = {
    mw: 6.8, depthKm: 12, lat: 37.75, lon: -122.55,
    strike: 325, dip: 90, seismogenicKm: 18, rupture: null,
    presetId: null, observed: null,
    site: null,               // {lat, lon, name}
    seed: 20260821,
    rec: null, sol: null, sc: null,
    anim: null, animT0: 0,
};

let mapView = null, citiesReady = false;

/* ─────────────────────────────  scenario  ───────────────────────────── */

function build() {
    state.sc = scenario({
        mw: state.mw, lat: state.lat, lon: state.lon, depthKm: state.depthKm,
        strike: state.strike, dip: state.dip,
        seismogenicKm: state.seismogenicKm, rupture: state.rupture,
    });
    return state.sc;
}

/** The site the answer card is about: the user's chosen place if they picked
 *  one, otherwise the largest city inside the shaken area — which is nearly
 *  always the one a reader wants to know about anyway. */
function resolveSite(sc) {
    if (state.site) {
        return { ...state.site, report: sc.siteReport(state.site.lat, state.site.lon) };
    }
    if (citiesReady) {
        const h = exposureByIntensity(sc, 4).headline;
        if (h) {
            return { lat: h.lat, lon: h.lon, name: h.name, pop: h.pop, auto: true,
                     report: sc.siteReport(h.lat, h.lon) };
        }
    }
    // Nothing else to talk about: report the ground directly above the rupture.
    return { lat: sc.lat, lon: sc.lon, name: null, auto: true, report: sc.siteReport(sc.lat, sc.lon) };
}

/* ───────────────────────────────  render  ──────────────────────────── */

function renderAnswer(sc, site) {
    const r = site.report;
    const deg = Math.max(1, Math.min(12, Math.round(r.mmi)));
    const band = mmiBand(r.mmi);
    const d = (CFG.degrees || {})[deg] || {};

    const big = $('eqBig');
    big.style.background = band.color;
    big.style.color = band.text;
    $('eqBigNum').textContent = deg;
    $('eqBigRoman').textContent = `${T.mmi_label} ${band.roman}`;
    $('eqBigPlace').textContent = site.name || '';

    $('eqDesc').textContent = d.long || d.short || '';

    const facts = [
        [T.km_from_epi, `${fmt(r.repiKm, r.repiKm < 20 ? 1 : 0)} ${T.km}`],
        [T.km_from_fault, `${fmt(r.rrupKm, r.rrupKm < 20 ? 1 : 0)} ${T.km}`],
        ['PGA', `${fmt((r.pgaG * 100), r.pgaG * 100 < 10 ? 2 : 1)} %g`],
        ['PGV', `${fmt(r.pgvCmS, r.pgvCmS < 10 ? 1 : 0)} ${T.unit_cms}`],
        [T.p_wave, `${fmt(r.arrivals.p, 1)} ${T.sec_short}`],
        [T.s_wave, `${fmt(r.arrivals.s, 1)} ${T.sec_short}`],
        [T.shaking_lasts, `${fmt(r.durationS, 0)} ${T.sec_short}`],
        [T.depth_km, `${fmt(state.depthKm, 0)} ${T.km}`],
    ];
    $('eqFacts').innerHTML = facts.map(([k, v]) =>
        `<div class="eq-fact"><span>${esc(k)}</span><b>${esc(v)}</b></div>`).join('');

    /* Two honesty notes, and they are not boilerplate — each fires on a real
       condition and says something the reader could not otherwise know. */
    const warns = [];
    if (r.outOfRange) {
        warns.push(T.out_of_range
            .replace(':min', IPE_VALID.magMin.toFixed(1))
            .replace(':max', IPE_VALID.magMax.toFixed(1))
            .replace(':dist', String(IPE_VALID.distMaxKm)));
    }
    if (state.observed != null) {
        const epi = Math.round(Math.max(1, Math.min(12, sc.mmiAt(sc.lat, sc.lon))));
        warns.push(T.observed_was
            .replace(':model', String(epi))
            .replace(':observed', String(state.observed)));
    }
    const w = $('eqWarn');
    w.innerHTML = warns.map(esc).join('<br>');
    w.hidden = warns.length === 0;
}

function renderCities(sc) {
    const el = $('eqCities');
    if (!citiesReady) { el.innerHTML = `<div class="eq-note">${esc(T.loading_cities)}</div>`; return; }
    const ex = exposureByIntensity(sc, 4);
    if (!ex.top.length) { el.innerHTML = `<div class="eq-note">${esc(T.no_cities)}</div>`; return; }

    el.innerHTML = ex.top.slice(0, 8).map((c) => {
        const deg = Math.max(1, Math.min(12, Math.round(c.mmi)));
        const b = MMI_SCALE[deg - 1];
        return `<div class="eq-city">
            <span class="b" style="background:${b.color};color:${b.text}">${deg}</span>
            <span class="n">${esc(c.name)}<small>${fmt(c.rrupKm, c.rrupKm < 20 ? 1 : 0)} ${esc(T.km)}</small></span>
            <span class="p">${esc(compact(c.pop))}</span>
        </div>`;
    }).join('');
}

function renderEnergy(sc) {
    const kt = sc.tntTons / 1000;
    const hiro = sc.energy / JOULES_HIROSHIMA;
    /* Real superscripts. "3.98·10^16" is what a plain text renderer produces
       and it reads as a typo; the digits exist in Unicode, so use them. */
    const SUP = { '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
                  '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
                  '-': '⁻' };
    const exp = (v) => {
        const [m, e] = v.toExponential(2).split('e');
        return `${m}·10${String(+e).split('').map((c) => SUP[c] || c).join('')}`;
    };
    const rows = [
        [T.e_moment, `${exp(sc.moment)} ${T.unit_nm}`],
        [T.e_energy, `${exp(sc.energy)} ${T.unit_j}`],
        [T.e_tnt, kt >= 1000 ? `${fmt(kt / 1000, 1)} ${T.unit_mt}` : `${fmt(kt, 1)} ${T.unit_kt}`],
        [T.e_hiroshima, fmt(hiro, hiro < 10 ? 1 : 0)],
        [T.e_length, `${fmt(sc.lengthKm, sc.lengthKm < 20 ? 1 : 0)} ${T.km}`],
        [T.e_width, `${fmt(sc.widthKm, 1)} ${T.km}`],
        [T.e_slip, `${fmt(sc.slipM, 2)} ${T.unit_m}`],
        [T.e_corner, `${fmt(sc.fc, 3)} ${T.unit_hz}`],
    ];
    $('eqEnergy').innerHTML = rows.map(([k, v]) =>
        `<div class="eq-fact"><span>${esc(k)}</span><b>${esc(v)}</b></div>`).join('');
}

function renderRecord(sc, site) {
    const r = site.report;
    const rec = seismogram({
        mw: sc.mw, fc: sc.fc, distKm: Math.max(1, r.rhypKm),
        durationS: r.durationS, pgaGal: r.pgaGal,
        dt: 0.01, seed: state.seed,
    });
    state.rec = rec;
    state.sol = Buildings.solve(rec);

    const sp = rec.tS - rec.tP;
    drawSeismogram($('eqSeismo'), rec, {
        t: T, durationS: r.durationS,
        spLabel: `S−P ${sp.toFixed(1)} ${T.sec_short} × ${KM_PER_SP_SECOND.toFixed(1)} = ${Math.round(sp * KM_PER_SP_SECOND)} ${T.km}`,
    });
    startAnimation();
}

function apply({ refit = false } = {}) {
    const sc = build();
    const site = resolveSite(sc);
    mapView && mapView.render(sc, site, T);
    if (refit) mapView && mapView.fit(sc, site);
    renderAnswer(sc, site);
    renderCities(sc);
    renderEnergy(sc);
    renderRecord(sc, site);
}

/* ─────────────────────────────  animation  ──────────────────────────── */

function startAnimation() {
    if (state.anim) cancelAnimationFrame(state.anim);
    const canvas = $('eqBuildings');
    if (!canvas || !state.rec) return;
    const rec = state.rec, sol = state.sol;
    // Loop over the interesting window rather than the padded buffer.
    const tEnd = Math.min(rec.seconds, rec.tS + Math.max(6, rec.seconds * 0.5));
    const tStart = Math.max(0, rec.tP - 0.6);
    state.animT0 = performance.now();

    let captionDone = false;
    const frame = (now) => {
        const elapsed = ((now - state.animT0) / 1000) % Math.max(1, tEnd - tStart);
        Buildings.draw(canvas, rec, sol, tStart + elapsed, T);
        if (!captionDone) {
            captionDone = true;
            const cap = $('eqExag');
            if (cap) cap.textContent = `${T.sway} ×${Math.round(Buildings.exaggeration())}.`;
        }
        state.anim = requestAnimationFrame(frame);
    };
    state.anim = requestAnimationFrame(frame);
}

/* Stop burning frames when nobody is looking — the page has two canvases and
   a Leaflet map, and a backgrounded tab animating an oscillator solution is
   pure battery cost. */
function watchVisibility() {
    const canvas = $('eqBuildings');
    if (!canvas || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
            if (e.isIntersecting && !state.anim && state.rec) startAnimation();
            else if (!e.isIntersecting && state.anim) { cancelAnimationFrame(state.anim); state.anim = null; }
        }
    }, { rootMargin: '120px' });
    io.observe(canvas);
    document.addEventListener('visibilitychange', () => {
        if (document.hidden && state.anim) { cancelAnimationFrame(state.anim); state.anim = null; }
        else if (!document.hidden && !state.anim && state.rec) startAnimation();
    });
}

/* ───────────────────────────────  inputs  ──────────────────────────── */

function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function setMag(v) {
    state.mw = Math.round(v * 10) / 10;
    $('eqMag').value = String(state.mw);
    $('eqMagOut').textContent = `M ${state.mw.toFixed(1)}`;
}
function setDepth(v) {
    state.depthKm = Math.round(v);
    $('eqDepth').value = String(state.depthKm);
    $('eqDepthOut').textContent = `${state.depthKm} ${T.km}`;
}

/** Leaving a preset means leaving its published rupture behind — otherwise a
 *  reader who nudges the magnitude keeps Tohoku's 500 km fault at M6 and the
 *  map quietly lies. */
function clearPreset() {
    if (!state.presetId) return;
    state.presetId = null;
    state.rupture = null;
    state.observed = null;
    for (const el of document.querySelectorAll('#eqPresets .eq-chip')) el.classList.remove('active');
}

function loadPreset(id) {
    const e = BY_ID[id];
    if (!e) return;
    const inp = toScenarioInput(e);
    Object.assign(state, {
        lat: inp.lat, lon: inp.lon, depthKm: inp.depthKm, strike: inp.strike,
        dip: inp.dip, seismogenicKm: inp.seismogenicKm, rupture: inp.rupture,
        presetId: id, observed: e.observed ?? null, site: null,
    });
    setMag(e.mw);
    setDepth(e.depthKm);
    $('eqCity').value = '';
    for (const el of document.querySelectorAll('#eqPresets .eq-chip')) {
        el.classList.toggle('active', el.dataset.id === id);
    }
    apply({ refit: true });
    report('preset', { id });
}

function buildPresetChips() {
    const wrap = $('eqPresets');
    const events = CFG.events || {};
    wrap.innerHTML = CATALOGUE.map((e) => {
        const name = (events[e.key] || {}).name || e.id;
        return `<button type="button" class="eq-chip" data-id="${esc(e.id)}">${esc(name)} · M${e.mw.toFixed(1)}</button>`;
    }).join('');
    wrap.addEventListener('click', (ev) => {
        const b = ev.target.closest('.eq-chip');
        if (b) loadPreset(b.dataset.id);
    });
}

/* ─────────────────────────────  city search  ───────────────────────── */

function wireCitySearch() {
    const input = $('eqCity'), box = $('eqSugg');
    let items = [], active = -1, seq = 0;

    const close = () => { box.classList.remove('open'); box.innerHTML = ''; items = []; active = -1; };

    const choose = (c) => {
        state.site = { lat: c.lat, lon: c.lng ?? c.lon, name: c.name, pop: c.pop };
        input.value = c.name;
        close();
        apply({ refit: true });
        report('site_pick', { how: 'search' });
    };

    input.addEventListener('input', () => {
        const q = input.value.trim();
        if (q.length < 2 || !window.LocationInput) { close(); return; }
        /* ⚠️ searchWithFallback ВСЕГДА возвращает промис (на промахе он ещё
           подтягивает файл малых городов РФ). Здесь его результат звали
           `.map()` — промис такого метода не имеет, вызов БРОСАЛ, а
           `.catch(close)` ниже это молча съедал и закрывал список. То есть
           поиск города не работал с запуска тула и не оставлял следов ни на
           экране, ни в консоли. */
        const mine = ++seq;
        Promise.resolve(window.LocationInput.loadData()).then(
            () => (window.LocationInput.searchWithFallback || window.LocationInput.search)(q, 7)
        ).then((hits) => {
            // Человек уже дописал дальше — ответ протух.
            if (mine !== seq) return;
            /* search() отдаёт ССЫЛКИ на строки набора — {i, score, pop}, а не
               города; имя и координаты даёт rowToCity(i). Здесь стояло
               `res.map((r) => (LI.rowToCity ? r : r))` — обе ветки возвращают
               ОДНО И ТО ЖЕ, то есть преобразование было написано пустышкой и
               выглядело выполненным. */
            items = (hits || []).map((h) => window.LocationInput.rowToCity(h.i));
            if (!items.length) { close(); return; }
            box.innerHTML = items.map((c, i) =>
                `<button type="button" role="option" data-i="${i}">${esc(c.name)}<small>${esc(c.cc || '')}</small></button>`).join('');
            box.classList.add('open');
            active = -1;
        }).catch(close);
    });

    box.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-i]');
        if (b) choose(items[+b.dataset.i]);
    });

    input.addEventListener('keydown', (e) => {
        if (!items.length) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            active = (active + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length;
            for (const b of box.querySelectorAll('button')) b.setAttribute('aria-selected', String(+b.dataset.i === active));
        } else if (e.key === 'Enter' && active >= 0) {
            e.preventDefault(); choose(items[active]);
        } else if (e.key === 'Escape') close();
    });

    document.addEventListener('click', (e) => {
        if (!box.contains(e.target) && e.target !== input) close();
    });
}

/**
 * Locate-me goes through the shared primitive, never through
 * `navigator.geolocation` directly.
 *
 * The API is simply ABSENT in hardened Firefox builds (geo.enabled=false, which
 * Mull, IronFox and arkenfox ship by default), and a hand-rolled call site
 * answers that with a button that does nothing and says nothing — invisible in
 * telemetry because nobody reports an optional button. `__geo` knows the
 * failure reasons, has the explanation strings in all nine locales, and renders
 * the panel; the tool only says where to put it.
 */
function wireLocate() {
    $('eqLocate').addEventListener('click', () => {
        const geo = window.__geo;
        const panel = $('eqGeoPanel');
        if (!geo) return;
        const pre = geo.available();
        if (!pre.ok) { geo.panel(panel, geo.explain(pre.reason)); return; }
        geo.get({ enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }).then((p) => {
            const { latitude: lat, longitude: lon } = p.coords;
            let name = null;
            if (citiesReady) {
                const near = citiesWithin(lat, lon, 60, 1);
                if (near.length) name = near[0].name;
            }
            state.site = { lat, lon, name };
            $('eqCity').value = name || `${lat.toFixed(3)}, ${lon.toFixed(3)}`;
            if (panel) panel.innerHTML = '';
            apply({ refit: true });
            report('site_pick', { how: 'geo' });
        }).catch((err) => geo.panel(panel, geo.explain(err)));
    });
}

/* ──────────────────────────────  telemetry  ────────────────────────── */

function report(stage, data) {
    try { window.__aeFunnel && window.__aeFunnel(stage, data || {}); } catch (e) {}
}

/* ────────────────────────────────  boot  ───────────────────────────── */

function boot() {
    if (!window.L) { return; }

    paintLegend($('eqLegendBar'));
    buildPresetChips();
    mapView = createMap($('eqMap'), CFG);
    mapView.onPick((lat, lon) => {
        const hint = $('eqPickHint');
        if (hint) hint.classList.add('gone');
        clearPreset();
        state.lat = lat; state.lon = lon;
        apply();
        report('epicenter_move', {});
    });

    $('eqMag').addEventListener('input', (e) => { clearPreset(); setMag(+e.target.value); apply(); });
    $('eqDepth').addEventListener('input', (e) => { clearPreset(); setDepth(+e.target.value); apply(); });
    $('eqMag').addEventListener('change', () => report('magnitude_set', { m: state.mw }));

    $('eqReroll').addEventListener('click', () => {
        state.seed = (state.seed * 1103515245 + 12345) >>> 0;
        apply();
        report('reroll', {});
    });

    const playBtn = $('eqPlay');
    playBtn.addEventListener('click', () => {
        if (isPlaying()) { stopAudio(); playBtn.textContent = T.listen; return; }
        if (state.rec && playRecord(state.rec, () => { playBtn.textContent = T.listen; })) {
            playBtn.textContent = T.stop;
            report('listen', { speedup: AUDIO_SPEEDUP });
        }
    });

    wireCitySearch();
    wireLocate();
    watchVisibility();

    // First paint with whatever we have, so the page is never empty; the city
    // dataset arrives afterwards and only improves it.
    loadPreset(CFG.home);
    mapView.invalidate();
    // Instructions that never leave become furniture; this one has said its
    // piece by the time a reader has looked at the map for ten seconds.
    setTimeout(() => { const h = $('eqPickHint'); h && h.classList.add('gone'); }, 10000);

    if (window.LocationInput) {
        Promise.resolve(window.LocationInput.loadData())
            .then(() => { citiesReady = buildIndex(); if (citiesReady) apply(); })
            .catch(() => {});
    }

    try { window.__stage && window.__stage('ready'); } catch (e) {}
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
